import React from "react";
import { Box, Grid2 } from "@mui/material";
import { useSwipeable } from "react-swipeable";

// --- Component Imports (assuming these are in the same directory) ---
import ImageTag from "./ImageTag";
import PhotoSphere from "./PhotoSphere";
import MainImage from "./MainImage";
import PlayButton from "./PlayButton";
import ImageTypeSelect from "./ImageTypeSelect";
import VideoFrameScrubber from "./VideoFrameScrubber";
import CopyIframeUrlButton from "./CopyIframeUrlButton";
import { useCarouselContext } from "../../context/CarouselContext";

const MainDisplay: React.FC = () => {
  const {
    filteredImages,
    selectedImage,
    selectedImageIndex,
    isWebGL,
    isPlaying,
    selectedType,
    uniqueImageTypes,
    sortedImages,
    handleGoTo,
    handleImageClick,
    handleTogglePlay,
    setSelectedType,
  } = useCarouselContext();

  const handlers = useSwipeable({
    onSwipedLeft: () => handleGoTo((selectedImageIndex + 1) % filteredImages.length),
    onSwipedUp: () => handleGoTo((selectedImageIndex + 1) % filteredImages.length),
    onSwipedRight: () => handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length),
    onSwipedDown: () => handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length),
    trackMouse: true,
  });

  const isUcOnly = (
    (uniqueImageTypes.length === 1 && ["img_uc_dewarp", "img_uc"].includes(filteredImages[0]?.imageType || '')) ||
    (uniqueImageTypes.length > 1 && ["img_uc_dewarp", "img_uc"].includes(selectedType))
  );

  const showPlayButton = ((uniqueImageTypes.length === 1 && filteredImages[0]?.imageType !== "img_pano") ||
    (uniqueImageTypes.length > 1 && selectedType !== "img_pano"));

  if (!selectedImage) {
    return null; // or a loading indicator
  }

  // If only undercarriage images are selected, render the VideoFrameScrubber
  if (isUcOnly) {
    return (
      <Grid2 sx={{ xs: 12 }} style={{ height: "100%", display: "flex", justifyContent: "center" }}>
        <VideoFrameScrubber />
      </Grid2>
    );
  }

  const isPano = selectedImage?.imageType === "img_pano";

  return (
    <Grid2 sx={{ xs: 12 }} style={{ height: "80%", display: "flex", justifyContent: "center" }}>
      <Box
        key={selectedImage.idx}
        {...(!isPano ? handlers : {})}
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          minHeight: "100%",
          touchAction: "pan-y",
          cursor: isPano && isWebGL ? "default" : "pointer",
        }}
        onClick={() => handleImageClick(selectedImage)}
      >
        {/* Overlaid UI Elements */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            alignItems: "center",
            gap: 1,
            zIndex: 10,
          }}
        >
          <ImageTag tag={selectedImage?.tag} />
          <CopyIframeUrlButton />
        </Box>

        <ImageTypeSelect
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          uniqueImageTypes={uniqueImageTypes}
          sortedImages={sortedImages}
          showPlayButton={showPlayButton}
        />

        {showPlayButton && (
          <PlayButton sliderPlaying={isPlaying} handleTogglePlay={handleTogglePlay} />
        )}

        {/* Main Content: PhotoSphere or standard Image */}
        {isPano && isWebGL ? (
          <PhotoSphere image={selectedImage?.src} type="img_pano" />
        ) : (
          <MainImage image={selectedImage?.src} index={selectedImage.idx} />
        )}

        {/* WebGL Not Supported Warning */}
        {isPano && !isWebGL && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              backgroundColor: "#ff4d4f",
              color: "white",
              textAlign: "center",
              padding: "8px 16px",
              fontWeight: 400,
              borderRadius: "4px",
              marginBottom: "8px",
              opacity: "0.8",
              zIndex: 10,
            }}
          >
            WebGL is not supported in your browser.
          </Box>
        )}
      </Box>
    </Grid2>
  );
};

export default MainDisplay;